import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import CountUp from '../components/ui/CountUp';
import * as FaIcons from 'react-icons/fa';

const AllocationItem = ({ label, percentage, color, desc, delay }) => (
  <motion.div 
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    className="mb-8 last:mb-0"
  >
    <div className="flex justify-between items-end mb-3">
      <h4 className="font-bold text-gray-800 text-lg">{label}</h4>
      <span className="font-heading font-bold text-2xl text-primary-dark">{percentage}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-4 mb-3 overflow-hidden shadow-inner">
      <motion.div 
        initial={{ width: 0 }}
        whileInView={{ width: `${percentage}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, ease: "easeOut", delay: delay + 0.2 }}
        className={`h-full rounded-full ${color}`}
      ></motion.div>
    </div>
    <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
  </motion.div>
);

const Impact = () => {
  return (
    <>
      <Helmet>
        <title>Impact & Transparency | AGIR USA</title>
        <meta name="description" content="See how your donations transform lives. We are committed to financial integrity, accountability, and measurable results in Guinea-Bissau." />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-primary-dark py-32 text-center text-white relative overflow-hidden min-h-[50vh] flex items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e06079f29fbfc9b22098a2faa4561dde.jpeg" 
            alt="Background pattern" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary-dark"></div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block py-1 px-4 border border-white/20 rounded-full text-xs font-bold tracking-[0.2em] uppercase mb-6 bg-white/5 backdrop-blur-sm">
              Accountability Matters
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6">Honoring Your Trust,<br />Transforming Their World</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed font-light">
              We believe that every dollar entrusted to us is a seed of hope. Here is how we steward your generosity with integrity.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Financial Stewardship Section */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-dark mb-6">Where Your Money Goes</h2>
            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              We pride ourselves on keeping administrative costs low so that your donation goes exactly where it's needed most—directly to the children and communities we serve.
            </p>
            
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-card border border-gray-100">
              <AllocationItem delay={0} label="Nutrition & Health Programs" percentage={42} color="bg-primary" desc="Procuring food, medical supplies, and clean water infrastructure." />
              <AllocationItem delay={0.2} label="Education & Vocational Training" percentage={35} color="bg-secondary" desc="Teacher salaries, school supplies, facility maintenance, and scholarships." />
              <AllocationItem delay={0.4} label="Community & Spiritual Care" percentage={15} color="bg-accent" desc="Pastoral support, youth programs, and community outreach events." />
              <AllocationItem delay={0.6} label="Administration & Fundraising" percentage={8} color="bg-gray-400" desc="Essential operations, oversight, and donor communication." />
            </div>
          </div>

          <div className="space-y-8">
            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-surface p-8 rounded-2xl border-l-4 border-primary shadow-soft"
            >
              <div className="flex items-start gap-4">
                <SafeIcon icon={FaIcons.FaHandshake} className="text-4xl text-primary mt-1" />
                <div>
                  <h3 className="font-bold text-xl text-primary-dark mb-2">Locally Led, Globally Supported</h3>
                  <p className="text-gray-600">
                    Our programs are run by local Guinean leaders who understand the culture and needs intimately. This ensures every initiative is relevant, respectful, and sustainable.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-surface p-8 rounded-2xl border-l-4 border-secondary shadow-soft"
            >
              <div className="flex items-start gap-4">
                <SafeIcon icon={FaIcons.FaFileContract} className="text-4xl text-secondary mt-1" />
                <div>
                  <h3 className="font-bold text-xl text-primary-dark mb-2">25+ Years of Consistency</h3>
                  <p className="text-gray-600">
                    Since 1998, we have maintained rigorous financial standards. We are committed to radical transparency and regular auditing to ensure full accountability.
                  </p>
                </div>
              </div>
            </motion.div>

            <div className="text-center pt-4">
              <Button variant="outline" size="lg" className="w-full sm:w-auto px-10">
                Download 2023 Annual Report (PDF)
              </Button>
            </div>
          </div>

        </div>
      </Section>

      {/* 1998 vs Today Comparison - Interactive */}
      <Section bg="white" className="py-20">
        <div className="bg-primary-dark rounded-3xl p-8 md:p-16 text-white relative overflow-hidden shadow-2xl">
          {/* Texture overlay */}
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          
          <div className="relative z-10 grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-secondary">A Journey of Growth</h2>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                What started as a small emergency response to civil war has grown into a nationwide movement of hope. Your support fuels this growth.
              </p>
              <Button to="/get-involved" variant="secondary" size="lg" className="shadow-glow">Help Us Do More</Button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {[
                { year: 1998, val: 50, label: "Children Fed Daily", color: "text-white" },
                { year: 2024, val: 1000, label: "Children Fed Daily", color: "text-secondary" },
                { year: 1998, val: 0, label: "Schools Built", color: "text-white" },
                { year: 2024, val: 3, label: "Education Centers", color: "text-secondary" }
              ].map((stat, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white/10 p-6 rounded-2xl backdrop-blur-md border border-white/10 hover:bg-white/20 transition-colors"
                >
                  <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${stat.year === 2024 ? 'text-secondary' : 'text-gray-400'}`}>{stat.year}</div>
                  <div className={`text-4xl font-heading font-bold mb-1 ${stat.color}`}>
                    {stat.val === 0 ? <span className="text-xl md:text-2xl">Hope was born</span> : <CountUp end={stat.val} />}
                  </div>
                  <div className="text-sm text-gray-300 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Impact;